/**
 * Pure layout/graph helpers for the org-chart diagram.
 * Kept free of React/React Flow rendering concerns so they're easy to reason about.
 */
import dagre from '@dagrejs/dagre'
import type { OrgChartNode } from '@/types/hr'

export const NODE_W = 240
export const NODE_H = 92
export type LayoutDir = 'TB' | 'LR'
export type GroupBy = 'none' | 'department' | 'country'

export interface SimpleEdge {
  source: string
  target: string
}

/** Map of id -> node and id -> parent id, derived from the manager tree. */
export function indexTree(roots: OrgChartNode[]): {
  byId: Map<string, OrgChartNode>
  parentOf: Map<string, string | null>
} {
  const byId = new Map<string, OrgChartNode>()
  const parentOf = new Map<string, string | null>()
  const walk = (n: OrgChartNode, parent: string | null) => {
    byId.set(n.id, n)
    parentOf.set(n.id, parent)
    ;(n.children ?? []).forEach((c) => walk(c, n.id))
  }
  roots.forEach((r) => walk(r, null))
  return { byId, parentOf }
}

/** All descendant ids of a node (excluding the node itself). */
export function descendantIds(node: OrgChartNode): Set<string> {
  const out = new Set<string>()
  const walk = (n: OrgChartNode) => {
    ;(n.children ?? []).forEach((c) => {
      out.add(c.id)
      walk(c)
    })
  }
  walk(node)
  return out
}

/** Ancestor ids of a node, nearest first. */
export function ancestorIds(id: string, parentOf: Map<string, string | null>): string[] {
  const out: string[] = []
  let cur = parentOf.get(id) ?? null
  while (cur) {
    out.push(cur)
    cur = parentOf.get(cur) ?? null
  }
  return out
}

export interface VisibleModel {
  /** Nodes that should be rendered. */
  nodes: OrgChartNode[]
  /** Parent→child edges among the visible nodes. */
  edges: SimpleEdge[]
  /** Node ids that are collapsed (have hidden children). */
  collapsed: Set<string>
}

/**
 * Determine which nodes are visible given collapse + focus state.
 * - Focus mode: show the focused node, its ancestors, and its full subtree.
 * - Otherwise: walk the tree but don't descend into a collapsed node.
 */
export function selectVisible(
  roots: OrgChartNode[],
  opts: { collapsedIds: Set<string>; focusId: string | null }
): VisibleModel {
  const { collapsedIds, focusId } = opts
  const nodes: OrgChartNode[] = []
  const edges: SimpleEdge[] = []
  const collapsed = new Set<string>()

  if (focusId) {
    const { byId, parentOf } = indexTree(roots)
    const focusNode = byId.get(focusId)
    if (focusNode) {
      const keep = new Set<string>([focusId])
      ancestorIds(focusId, parentOf).forEach((a) => keep.add(a))
      descendantIds(focusNode).forEach((d) => keep.add(d))
      const walk = (n: OrgChartNode) => {
        if (!keep.has(n.id)) return
        nodes.push(n)
        ;(n.children ?? []).forEach((c) => {
          if (keep.has(c.id)) {
            edges.push({ source: n.id, target: c.id })
            walk(c)
          }
        })
      }
      roots.forEach(walk)
      return { nodes, edges, collapsed }
    }
  }

  const walk = (n: OrgChartNode) => {
    nodes.push(n)
    const kids = n.children ?? []
    if (kids.length > 0 && collapsedIds.has(n.id)) {
      collapsed.add(n.id)
      return
    }
    kids.forEach((c) => {
      edges.push({ source: n.id, target: c.id })
      walk(c)
    })
  }
  roots.forEach(walk)
  return { nodes, edges, collapsed }
}

/** Dagre hierarchical layout → map of id -> top-left position. */
export function dagreLayout(
  nodeIds: string[],
  edges: SimpleEdge[],
  dir: LayoutDir
): Map<string, { x: number; y: number }> {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: dir, nodesep: 30, ranksep: 70, marginx: 20, marginy: 20 })
  nodeIds.forEach((id) => g.setNode(id, { width: NODE_W, height: NODE_H }))
  edges.forEach((e) => g.setEdge(e.source, e.target))
  dagre.layout(g)
  const pos = new Map<string, { x: number; y: number }>()
  nodeIds.forEach((id) => {
    const n = g.node(id)
    // dagre returns center coords; React Flow positions are top-left.
    pos.set(id, { x: n.x - NODE_W / 2, y: n.y - NODE_H / 2 })
  })
  return pos
}

/** Vertical gap between stacked root trees / grid rows. */
const V_GAP = 60
/** Horizontal gap between orphan cards in the grid. */
const H_GAP = 30
/**
 * Intentional org-specific ordering: these root trees are stacked first, in this
 * order (full name, lowercased), so the chart reads Paul Walker's tree above
 * Jeff Mills's. Any root not listed falls back to descending subtree size, so
 * this never breaks if the data changes.
 */
const ROOT_PRIORITY = ['paul walker', 'jeff mills']

/**
 * Hierarchy ("forest") layout: lay out each root's tree as its own block with
 * dagre, then STACK the blocks vertically (instead of dagre's default of putting
 * every root in the same top rank, side-by-side). Multi-person trees stack top
 * to bottom (ROOT_PRIORITY first, then largest first); single-person "orphan"
 * roots are packed into a compact grid beneath them. Returns id -> top-left, the
 * same shape as dagreLayout, so callers are unchanged.
 */
export function forestLayout(
  nodes: OrgChartNode[],
  edges: SimpleEdge[],
  dir: LayoutDir
): Map<string, { x: number; y: number }> {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const childrenOf = new Map<string, string[]>()
  const isTarget = new Set<string>()
  for (const e of edges) {
    if (!childrenOf.has(e.source)) childrenOf.set(e.source, [])
    childrenOf.get(e.source)!.push(e.target)
    isTarget.add(e.target)
  }
  // Roots = visible nodes that are never an edge target.
  const rootIds = nodes.map((n) => n.id).filter((id) => !isTarget.has(id))

  interface Block {
    rootId: string
    ids: string[]
    edges: SimpleEdge[]
    pos: Map<string, { x: number; y: number }>
    width: number
    height: number
  }
  const blocks: Block[] = rootIds.map((rootId) => {
    // Collect this component's nodes + edges by walking down from the root.
    const ids: string[] = []
    const compEdges: SimpleEdge[] = []
    const walk = (id: string) => {
      ids.push(id)
      for (const c of childrenOf.get(id) ?? []) {
        compEdges.push({ source: id, target: c })
        walk(c)
      }
    }
    walk(rootId)
    const local = dagreLayout(ids, compEdges, dir)
    // Normalise so the block's top-left is (0, 0); measure width/height.
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const id of ids) {
      const p = local.get(id)!
      minX = Math.min(minX, p.x)
      minY = Math.min(minY, p.y)
      maxX = Math.max(maxX, p.x + NODE_W)
      maxY = Math.max(maxY, p.y + NODE_H)
    }
    const pos = new Map<string, { x: number; y: number }>()
    for (const id of ids) {
      const p = local.get(id)!
      pos.set(id, { x: p.x - minX, y: p.y - minY })
    }
    return { rootId, ids, edges: compEdges, pos, width: maxX - minX, height: maxY - minY }
  })

  const priorityOf = (b: Block) => {
    const r = byId.get(b.rootId)
    const name = r ? `${r.first_name} ${r.last_name}`.toLowerCase() : ''
    const idx = ROOT_PRIORITY.indexOf(name)
    return idx === -1 ? Infinity : idx
  }
  const trees = blocks
    .filter((b) => b.ids.length > 1)
    .sort((a, b) => {
      const pa = priorityOf(a)
      const pb = priorityOf(b)
      if (pa !== pb) return pa - pb
      if (b.ids.length !== a.ids.length) return b.ids.length - a.ids.length
      const an = byId.get(a.rootId)
      const bn = byId.get(b.rootId)
      return `${an?.last_name} ${an?.first_name}`.localeCompare(
        `${bn?.last_name} ${bn?.first_name}`
      )
    })
  const orphans = blocks
    .filter((b) => b.ids.length === 1)
    .sort((a, b) => {
      const an = byId.get(a.rootId)
      const bn = byId.get(b.rootId)
      return `${an?.last_name} ${an?.first_name}`.localeCompare(
        `${bn?.last_name} ${bn?.first_name}`
      )
    })

  const out = new Map<string, { x: number; y: number }>()
  let cursorY = 0
  let maxTreeWidth = NODE_W
  for (const b of trees) {
    maxTreeWidth = Math.max(maxTreeWidth, b.width)
    for (const id of b.ids) {
      const p = b.pos.get(id)!
      out.set(id, { x: p.x, y: cursorY + p.y })
    }
    cursorY += b.height + V_GAP
  }

  // Orphan grid beneath the trees, filling the same width as the widest tree.
  const cols = Math.max(1, Math.floor(maxTreeWidth / (NODE_W + H_GAP)))
  orphans.forEach((b, i) => {
    const col = i % cols
    const row = Math.floor(i / cols)
    out.set(b.rootId, {
      x: col * (NODE_W + H_GAP),
      y: cursorY + row * (NODE_H + V_GAP),
    })
  })

  return out
}

/**
 * Root ids in the same stacking order forestLayout uses (priority trees first,
 * then largest, then orphans) — so the diagram can frame the top tree on open.
 */
export function orderedRootIds(roots: OrgChartNode[]): string[] {
  const priorityOf = (r: OrgChartNode) => {
    const idx = ROOT_PRIORITY.indexOf(`${r.first_name} ${r.last_name}`.toLowerCase())
    return idx === -1 ? Infinity : idx
  }
  const cmpName = (a: OrgChartNode, b: OrgChartNode) =>
    `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)
  const trees = roots
    .filter((r) => (r.children?.length ?? 0) > 0)
    .sort((a, b) => {
      const p = priorityOf(a) - priorityOf(b)
      if (p !== 0) return p
      const s = descendantIds(b).size - descendantIds(a).size
      return s !== 0 ? s : cmpName(a, b)
    })
  const orphans = roots.filter((r) => (r.children?.length ?? 0) === 0).sort(cmpName)
  return [...trees, ...orphans].map((r) => r.id)
}

export interface GroupBox {
  key: string
  label: string
  x: number
  y: number
  width: number
  height: number
}

/**
 * Grouped layout: arrange nodes in columns by department or country, ignoring
 * the reporting hierarchy (mirrors the standalone tool's grouped views).
 */
export function groupedLayout(
  nodes: OrgChartNode[],
  groupBy: Exclude<GroupBy, 'none'>
): { pos: Map<string, { x: number; y: number }>; groups: GroupBox[] } {
  const COL_GAP = 60
  const ROW_GAP = 16
  const HEADER = 44
  const groupsMap = new Map<string, OrgChartNode[]>()
  for (const n of nodes) {
    const key = (groupBy === 'department' ? n.department_name : n.country) || 'Unassigned'
    if (!groupsMap.has(key)) groupsMap.set(key, [])
    groupsMap.get(key)!.push(n)
  }
  const sortedKeys = [...groupsMap.keys()].sort((a, b) => a.localeCompare(b))
  const pos = new Map<string, { x: number; y: number }>()
  const groups: GroupBox[] = []
  let colX = 0
  for (const key of sortedKeys) {
    const members = groupsMap.get(key)!
    members.sort((a, b) =>
      `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)
    )
    let rowY = HEADER
    members.forEach((m) => {
      pos.set(m.id, { x: colX, y: rowY })
      rowY += NODE_H + ROW_GAP
    })
    groups.push({
      key,
      label: `${key} (${members.length})`,
      x: colX - 12,
      y: 0,
      width: NODE_W + 24,
      height: rowY + 8,
    })
    colX += NODE_W + COL_GAP
  }
  return { pos, groups }
}
